import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { ComponentProps } from 'react';
import { GripVertical, MoreHorizontal, MessageSquarePlus, Copy, Share2, Archive, CheckCircle, Loader2 } from 'lucide-react';
import { MarkdownPreview } from '@/components/features/markdown-preview';
import { useInternalMarkdownLinkClick } from '@/lib/internal-link-navigation';
import { toast } from 'sonner';
import { testIds } from '@/lib/test-ids';
import { cn } from '@/lib/utils';
import {
  QUIET_MENU_DESTRUCTIVE_ITEM_CLASS,
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { RoadmapItem, Section, RoadmapViewMode } from './product-board-types';
import { sectionColors, moveToColumnMenuIcon } from './product-board-types';
import { normalizeBetDocFilePath, betDocumentReferenceId, getRawBetFilePath, getBetFileName, buildMcpPathSnippet, buildDelegatePrompt, isDispatchReady, isShapingNeeded, isDispatchBlocked } from './product-board.utils';

export function ProductBoardMarkdownPreview({
  linkBasePath,
  ...rest
}: ComponentProps<typeof MarkdownPreview> & { linkBasePath?: string }) {
  const onInternalLinkClick = useInternalMarkdownLinkClick(linkBasePath);
  return <MarkdownPreview {...rest} onInternalLinkClick={onInternalLinkClick} />;
}

export function RoadmapLoadingSkeleton({ mode }: { mode: RoadmapViewMode }) {
  if (mode === 'rows') {
    return (
      <div className="w-full min-w-0 space-y-4" data-testid={testIds.productBoard.roadmapSkeleton}>
        {['NOW', 'NEXT', 'LATER', 'DONE'].map((label) => (
          <div key={label} className="border border-border rounded-lg bg-card/40 overflow-hidden">
            <div className="px-4 py-3 flex items-center gap-3 border-b border-border/60">
              <div className="h-4 w-4 rounded bg-muted animate-pulse" aria-hidden />
              <div className="h-6 w-28 bg-muted rounded-md animate-pulse" aria-hidden />
              <div className="h-5 w-24 bg-muted/70 rounded-full animate-pulse" aria-hidden />
            </div>
            <div className="px-4 py-4 space-y-3">
              <div className="h-16 rounded-lg bg-muted/35 animate-pulse" aria-hidden />
              <div className="h-16 rounded-lg bg-muted/35 animate-pulse" aria-hidden />
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div
      className="flex w-full min-w-0 gap-6 overflow-x-auto pb-4"
      data-testid={testIds.productBoard.roadmapSkeleton}
      aria-busy
      aria-label="Loading roadmap"
    >
      {['NOW', 'NEXT', 'LATER', 'DONE'].map((label) => (
        <div key={label} className="min-w-[280px] max-w-[440px] flex-[1_0_280px] shrink-0 space-y-4">
          <div className="flex items-center gap-2">
            <div className="h-7 w-16 bg-muted rounded-md animate-pulse" aria-hidden />
            <div className="h-6 w-24 bg-muted/70 rounded-full animate-pulse" aria-hidden />
          </div>
          <div className="h-3 w-20 bg-muted/50 rounded animate-pulse" aria-hidden />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-[92px] rounded-lg border border-border bg-muted/25 animate-pulse"
                aria-hidden
              />
            ))}
          </div>
          <div className="h-9 rounded-lg bg-muted/20 animate-pulse" aria-hidden />
        </div>
      ))}
    </div>
  );
}

interface SortableBetCardProps {
  item: RoadmapItem;
  sectionId: string;
  isAnimating: boolean;
  isDragging?: boolean;
  onOpenInPanel: (id: string) => void;
  otherSections: Section[];
  onMoveToSection: (itemId: string, fromSection: string, toSection: string) => void;
  onArchive?: (itemId: string) => void;
  isInDoneColumn?: boolean;
  delegateMcpOrganizationId?: string | null;
  searchDimmed?: boolean;
  isActive?: boolean;
  onDispatchToCloud?: (item: RoadmapItem) => void;
  onPromoteFeedback?: (item: RoadmapItem) => void;
  isDispatching?: boolean;
  isDispatched?: boolean;
}

export function SortableBetCard({ 
  item, 
  sectionId, 
  isAnimating, 
  isDragging,
  onOpenInPanel,
  otherSections,
  onMoveToSection,
  onArchive,
  isInDoneColumn = false,
  delegateMcpOrganizationId = null,
  searchDimmed = false,
  isActive = false,
  onDispatchToCloud,
  onPromoteFeedback,
  isDispatching = false,
  isDispatched = false,
}: SortableBetCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ 
    id: item.id,
    data: { 
      type: 'bet-card',
      item,
      sectionId,
    }
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'high': return 'border-l-red-500';
      case 'medium': return 'border-l-yellow-500';
      default: return 'border-l-gray-500';
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'needs-shaping':
        return <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500 border border-amber-500/20 font-medium leading-none">Needs Shaping</span>;
      case 'shaped':
        return <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border font-medium leading-none">Shaped</span>;
      case 'in_progress':
        return <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 font-medium leading-none">In Progress</span>;
      case 'complete':
        return <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-500 border border-green-500/20 font-medium leading-none">Complete</span>;
      default:
        return null;
    }
  };

  const handleNativeDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    const docPath = normalizeBetDocFilePath(item.path);
    if (!docPath) {
      e.preventDefault();
      return;
    }
    const dragData = JSON.stringify({
      type: 'document',
      id: item.id,
      title: item.title,
      name: item.title,
      documentType: 'bet',
      path: docPath,
    });
    e.dataTransfer.setData('application/json', dragData);
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-bet-id={item.id}
      draggable={!!normalizeBetDocFilePath(item.path)}
      onDragStart={handleNativeDragStart}
      className={`group border rounded-lg p-4 bg-card hover:border-primary/40 hover:shadow-sm transition-all duration-200 cursor-pointer relative ${isActive ? 'border-primary bg-primary/10 ring-1 ring-primary/30 shadow-md' : 'border-border'} ${isAnimating ? 'ring-2 ring-primary ring-offset-2 animate-pulse' : ''} ${isSortableDragging ? 'shadow-lg z-50' : ''} ${searchDimmed ? 'opacity-40' : isInDoneColumn ? 'opacity-60' : ''}`}
      onClick={() => onOpenInPanel(item.id)}
      title={normalizeBetDocFilePath(item.path) ? 'Drag handle to reorder • Drag card to chat • Click to view' : 'Drag to reorder • Click to view details'}
    >
      <div 
        {...attributes}
        {...listeners}
        className="absolute left-1.5 top-4 opacity-0 group-hover:opacity-40 hover:!opacity-100 transition-opacity cursor-grab active:cursor-grabbing p-0.5"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />
      </div>

      <div className="flex items-start gap-1 pl-4 pr-0.5 mb-3">
        <h3
          className="font-medium text-sm leading-snug text-foreground flex-1 min-w-0 mr-1 break-words line-clamp-4"
          title={item.title}
        >
          {item.title}
        </h3>

        {/* Dispatched indicator — shown when a cloud agent is running this bet */}
        {(isDispatching || isDispatched) && (
          <span
            className="flex-shrink-0 flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 font-medium leading-none"
            title={isDispatching ? 'Dispatching to cloud agent…' : 'Cloud agent running'}
          >
            {isDispatching
              ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
              : <span className="animate-pulse">⚡</span>
            }
          </span>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
              onClick={(e) => e.stopPropagation()}
              title="Actions"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
            collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
            onClick={(e) => e.stopPropagation()}
          >
            {item.type === 'pilot-feedback' && onPromoteFeedback && (
              <>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onPromoteFeedback(item);
                  }}
                  className="gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  Set promoted_to…
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {normalizeBetDocFilePath(item.path) && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  const docPath = normalizeBetDocFilePath(item.path)!;
                  window.dispatchEvent(new CustomEvent('add-to-chat', {
                    detail: {
                      type: 'document',
                      id: betDocumentReferenceId(item),
                      title: item.title,
                      name: item.title,
                      documentType: 'bet',
                      path: docPath,
                    }
                  }));
                  toast.success(`Added to chat`, { description: item.title });
                }}
                className="gap-2"
              >
                <MessageSquarePlus className="w-4 h-4" />
                Add to chat
              </DropdownMenuItem>
            )}

            {normalizeBetDocFilePath(item.path) && onDispatchToCloud && (
              isDispatchReady(item) ? (
                <>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onDispatchToCloud(item);
                    }}
                    disabled={isDispatching}
                    className="gap-2"
                  >
                    {isDispatching
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <span className="text-sm leading-none">⚡</span>
                    }
                    Dispatch to Cloud Agent
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              ) : isShapingNeeded(item) ? (
                <>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      const docPath = normalizeBetDocFilePath(item.path)!;
                      if (!item.documentId) {
                        toast.warning('Bet not indexed yet', { description: 'Chat will resolve by file path if possible.' });
                      }
                      window.dispatchEvent(new CustomEvent('add-to-chat', {
                        detail: { type: 'document', id: betDocumentReferenceId(item), title: item.title, name: item.title, documentType: 'bet', path: docPath },
                      }));
                      toast.success('Bet added to chat', { description: 'Ask me to help shape it' });
                    }}
                    className="gap-2"
                  >
                    <span className="text-sm leading-none">🔷</span>
                    Shape in chat
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              ) : isDispatchBlocked(item) ? (
                <>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <DropdownMenuItem disabled className="gap-2 opacity-50 cursor-not-allowed">
                            <span className="text-sm leading-none">⚡</span>
                            Dispatch to Cloud Agent
                          </DropdownMenuItem>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p className="text-xs">Set <code>safe_to_dispatch: true</code> in the bet doc to enable</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <DropdownMenuSeparator />
                </>
              ) : null
            )}

            {item.path && (
              <>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    const name = getBetFileName(item.path);
                    if (name) { navigator.clipboard.writeText(name); toast.success('Filename copied', { description: name }); }
                  }}
                  className="gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy filename
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    const p = getRawBetFilePath(item.path);
                    if (p) { navigator.clipboard.writeText(p); toast.success('Path copied', { description: p }); }
                  }}
                  className="gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy path
                </DropdownMenuItem>
                {normalizeBetDocFilePath(item.path) && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      const s = buildMcpPathSnippet(item.path, delegateMcpOrganizationId);
                      if (s) {
                        navigator.clipboard.writeText(s);
                        toast.success('MCP lookup copied', { description: s });
                      }
                    }}
                    className="gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Copy MCP lookup
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(buildDelegatePrompt(item, delegateMcpOrganizationId));
                    toast.success('Delegate prompt copied');
                  }}
                  className="gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  Copy delegate prompt
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}

            {otherSections.filter(s => s.id !== 'done').map((section) => {
              const colors = sectionColors[section.color];
              const MoveIcon = moveToColumnMenuIcon(sectionId, section.id);
              return (
                <DropdownMenuItem
                  key={section.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveToSection(item.id, sectionId, section.id);
                  }}
                  className="gap-2"
                >
                  <MoveIcon className={`w-4 h-4 ${colors.text}`} />
                  Move to {section.title}
                </DropdownMenuItem>
              );
            })}

            {!isInDoneColumn && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  const doneSection = otherSections.find(s => s.id === 'done');
                  if (doneSection) onMoveToSection(item.id, sectionId, 'done');
                }}
                className="gap-2"
              >
                <CheckCircle className="w-4 h-4 text-purple-500" />
                Mark as done
              </DropdownMenuItem>
            )}

            {isInDoneColumn && onArchive && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchive(item.id);
                  }}
                  className={cn('gap-2', QUIET_MENU_DESTRUCTIVE_ITEM_CLASS)}
                >
                  <Archive className="w-4 h-4" />
                  Archive
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex items-center gap-1.5 pl-4 flex-wrap">
        {item.appetite && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border font-medium leading-none">
            {item.appetite}
          </span>
        )}
        {getStatusBadge(item.status)}
      </div>

      {item.progress !== undefined && item.progress > 0 && (
        <div className="mt-3 pl-4">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1.5">
            <span>{item.mustHaveTotal ? `${item.mustHaveDone ?? 0}/${item.mustHaveTotal} must-haves` : 'Progress'}</span>
            <span className="font-medium">{item.progress}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                item.progress >= 80 ? 'bg-green-500' :
                item.progress >= 40 ? 'bg-blue-500' :
                'bg-yellow-500'
              }`}
              style={{ width: `${item.progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

interface DroppableSectionProps {
  section: Section;
  children: React.ReactNode;
  isOver?: boolean;
}

export function DroppableSection({ section, children, isOver }: DroppableSectionProps) {
  const { setNodeRef, isOver: isDropOver } = useDroppable({
    id: `section-drop-${section.id}`,
    data: {
      type: 'section',
      sectionId: section.id,
    }
  });

  const showDropIndicator = isOver || isDropOver;

  return (
    <div 
      ref={setNodeRef}
      className={`space-y-4 min-h-[120px] transition-all duration-200 rounded-lg ${showDropIndicator ? 'bg-primary/5 ring-2 ring-primary/30 ring-dashed p-2 -m-2' : ''}`}
    >
      {children}
      {showDropIndicator && (
        <div className="h-16 border-2 border-dashed border-primary/40 rounded-lg flex items-center justify-center text-sm text-primary/60">
          Drop here
        </div>
      )}
    </div>
  );
}
